import React, {useState, useEffect} from 'react'
import styled from 'styled-components'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import {useToasts} from 'react-toast-notifications'
import {Row, Col, Card as CardBase, CardImg, CardBody, CardSubtitle, CardFooter} from 'reactstrap'
import addFavorite from 'lib/actions/add_favorite'
import removeFavorite from 'lib/actions/remove_favorite'
import thumbnailNotAvailable from 'img/image-not-available.png'
import Modal from 'ui/modal'
import ApiFetcher from 'lib/api_fetcher'
import {CircleFlagLanguage} from 'react-circle-flags'
import {tourTypeColors} from 'containers/list/classes'

const Wrapper = styled.div.attrs({
  className: 'col card-wrapper'
})``

const Card = styled(CardBase).attrs({
  className: 'rounded-3'
})`
  transition: 0.15s;
  border-color: var(--bs-gray-300);
  overflow: hidden;

  ${props => props.isBestSeller &&`
    /* border-color: var(--bs-yellow); */
  `}

  &:hover {
    border-color: var(--bs-primary);

    img {
      transform: scale(1.05);
    }

    .product-title {
      color: var(--bs-primary);
    }
  }
`

const Badge = styled.div.attrs({
  className: 'px-2 shadow rounded-3 rounded-bottom-0'
})`
  position: absolute;
  top: 0em;
  left: 0em;
  opacity: 1;
  z-index: 1;
  font-weight: bold;
  border-top-right-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
`

const BestSellerBadge = styled(Badge).attrs({
  className: 'text-white bg-success'
})``

const LanguageIcon = styled.div.attrs({
  className: ''
})`
  position: absolute;
  top: 7px;
  right: 7px;
  border-radius: 3em;
  border: 5px solid white;
  z-index: 1;
`

const Thumbnail = styled(CardImg).attrs({
  className: 'rounded-3 rounded-bottom-0'
})`
  ${props => props.status == 'active' &&`
    cursor: pointer;
    transition: 0.3s;
    opacity: 1;

    // &:hover {
    //   -webkit-transform: scale(1.01);
    //   transform: scale(1.01);
    // }
  `}

  ${props => props.status != 'active' &&`
    filter: grayscale(100%);
    opacity: 0.8;
  `}
`

const ProductTitle = styled.h5.attrs({
  className: 'my-1 fw-semibold product-title'
})`
  font-size: 1.1rem;

  a {
    text-decoration: none;
    color: var(--secondary);
    transition: .5s ease;
  }
`

const ProductFooter = styled(CardFooter).attrs({
  className: 'py-2 px-3 rounded-3 rounded-top-0'
})`
  transition: .5s ease;
`

const Icon = styled.i.attrs()`
  cursor: pointer;
  color: var(--bs-primary);
  font-size: 1.05rem;
  transition: .5s ease;

  &:hover {
    color: var(--secondary)
  }
`

const FeaturedPrice = styled.span.attrs()`
  cursor: pointer;
  transition: .3s ease;
  border: 0px;
  border-radius: 0.15em;
  font-size: 0.9em;

  strong {
    font-size: 1.3em;
  }

  &:hover {
    color: var(--bs-primary);
  }
`

const FavButton = styled(Icon).attrs({
  className: 'fa fa-heart me-1'
})`
  ${props => props.active &&`
    color: var(--bs-red);
  `}
`

const TemplateButton = styled(Icon).attrs({
  className: 'fa fa-info-circle'
})``

const NotificationButton = styled(Icon).attrs({
  className: 'fa fa-exclamation-triangle me-1'
})``

const EvoucherButton = styled(Icon).attrs({
  className: 'fa fa-mobile-alt'
})`
  padding: 0 0.27em;
  font-size: 1.1em;
`

const VariantsModal = (props) => {
  const {product, modal, toggle, urls, variantsUrl, variants} = props
  const {i18n} = useTranslation()

  if (!variants) return ''

  return (
    <Modal
      isOpen={modal}
      toggle={toggle}
      header={i18n.t('variants_modal.title')}
      closeOverlay={true}
    >
      <Row className={cn(['row row-cols-1 row-cols-md-2 row-cols-lg-3 mx-0', variants.length > 3 ? 'justify-content-start' : 'justify-content-center'])}>
        {variants.map((variant) => (
          <Col key={variant.id}>
            <ProductCard
              {...variant}
              url={urls.service.replace(/ *\:[^)]*\: */g, variant.slug)}
            />
          </Col>
        ))}
      </Row>
    </Modal>
  )
}

const fetchVariants = (url) => {
  return new ApiFetcher({
    key: 'variants',
    endpoint: url
  }).call()
}

const ProductLink = (props) => {
  const {product, setModal, variantsUrl, setVariants} = props
  const {status, has_variants, title, url} = product

  if (status != 'active') return <a>{title}</a>

  if (has_variants) {
    return (
      <a
        onClick={(e) => {
          e.preventDefault()
          PubSub.publish('loading-page')
          setVariants(null)
          fetchVariants(variantsUrl).then((res) => {
            PubSub.publish('loaded-page')
            setVariants(res)
          })
          setModal(true)
        }}
        href=""
      >
        {title}
      </a>
    )
  }

  return <a href={url}>{title}</a>
}

const onProductClick = (product, setModal, variantsUrl, setVariants) => {
  const {status, has_variants, url} = product

  if (status != 'active') return ''

  if (has_variants) {
    PubSub.publish('loading-page')
    setVariants(null)
    fetchVariants(variantsUrl).then((res) => {
      PubSub.publish('loaded-page')
      setVariants(res)
    })
    setModal(true)
  } else {
    window.location.href = url
  }
}

const capitalize = ([firstLetter, ...restOfWord]) => firstLetter.toUpperCase() + restOfWord.join("")

const CardContent = (props) => {
  const {id, has_variants, thumbnail, title, city_label, languages, status, tour_type, tour_type_label, featured_price, featured_price_label, notification, evoucher, url, template_link, best_seller, urls} = props

  const [modal, setModal] = useState(false)
  const [variants, setVariants] = useState()
  const toggle = () => setModal(!modal)
  const [favorite, setFavorite] = useState(props.favorite)
  const [isHover, setIsHover] = useState(false)
  const {i18n} = useTranslation()
  const {addToast} = useToasts()

  const onAddFavorite = () => {
    setFavorite(true)
    addToast(i18n.t('product_card.added_to_favorites_toast'), {appearance: 'success', autoDismiss: true})
  }

  const onRemoveFavorite = () => {
    setFavorite(false)
    addToast(i18n.t('product_card.removed_from_favorites_toast'), {appearance: 'info', autoDismiss: true})
  }

  let variantsUrl = urls ? urls.variants.replace(':id:', id) : null

  return (
    <>
      <VariantsModal
        product={props}
        modal={modal}
        toggle={toggle}
        urls={urls}
        variantsUrl={variantsUrl}
        variants={variants}
      />
      <Card isBestSeller={best_seller} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)} className={cn(['h-100', isHover ? 'shadow' : 'shadow-sm'])}>
        {false && <BestSellerBadge>{i18n.t('product_card.best_seller_badge')}</BestSellerBadge>}

        {languages && languages.length == 1 &&
          <LanguageIcon>
            <CircleFlagLanguage
              languageCode={languages[0].code}
              height="32"
              title={i18n.t('product_card.only_available_in_language', {language: capitalize(languages[0].code)})}
              style={{cursor: 'pointer'}}
            />
          </LanguageIcon>
        }

        <Thumbnail
          top
          width="100%"
          status={status}
          src={thumbnail ? thumbnail : thumbnailNotAvailable}
          onClick={(e) => {
            e.preventDefault()
            onProductClick(props, setModal, variantsUrl, setVariants)
          }}
        />
        <CardBody>
          <ProductTitle>
            <ProductLink
              product={props}
              setModal={setModal}
              setVariants={setVariants}
              variantsUrl={variantsUrl}
            />
          </ProductTitle>
        </CardBody>
        <div className="d-flex justify-content-between p-3 pt-0">
          <div>
            <small>{city_label}</small>
          </div>
          {tour_type_label &&
            <div>
              <small className={cn("px-2 py-1 rounded fw-semibold", tourTypeColors(tour_type))} style={{fontSize: '0.75em'}}>{tour_type_label}</small>
            </div>
          }
        </div>
        <ProductFooter className={isHover ? 'bg-light' : 'bg-white'}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="btn-group">
              <FavButton
                active={favorite}
                title={favorite ? i18n.t('product_card.remove_fav') : i18n.t('product_card.add_tooltip')}
                onClick={() => favorite ? removeFavorite(id, onRemoveFavorite) : addFavorite(id, onAddFavorite)}
              />
              {template_link &&
                <TemplateButton
                  title={i18n.t('product_card.template_link_tooltip')}
                  onClick={() => window.open(template_link)}
                />
              }
              {notification && <NotificationButton title="notification" />}
              {evoucher && <EvoucherButton title={i18n.t('product_card.evoucher_tooltip')} />}
            </div>
            <div className={isHover ? 'text-primary' : 'text-secondary'}>
              {status == 'active' ? (
                featured_price ? (
                  <FeaturedPrice onClick={() => status == 'active' ? (window.location = url) : null}>
                    {i18n.t('product_card.from')} <strong className="h3 fw-bold">{featured_price_label}</strong>
                  </FeaturedPrice>
                ) : (
                  <strong className="h3" style={{fontSize: '1.22em'}}>
                    {i18n.t('product_card.no_feature_price_label')}
                  </strong>
                )
              ) : (
                <div className="py-2">
                  <i className="text-muted">{i18n.t('product_card.inactive')}</i>
                </div>
              )}
            </div>
          </div>
        </ProductFooter>
      </Card>
    </>
  )
}

const ProductCard = (props) => {
  if (props.hideWrapper) return <CardContent {...props}/>

  return (
    <Wrapper>
      <CardContent {...props}/>
    </Wrapper>
  )
}

export default ProductCard
